import { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { RadioButton } from "primereact/radiobutton";
import { Dialog } from "primereact/dialog";
import { getFicha as getBankAccounts } from "../../../services/backend/bankAccountService";
import { getCompany } from "../../../config/companyConfig";

const CheckoutPaymentSection = ({
  onPayWithGetnet,
  onPayWithCredito,
  paymentLoading,
  isPaymentBusy,
  isPaymentReady,
  appCanal,
}) => {
  const [selectedMethod, setSelectedMethod] = useState("getnet");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [showBankDialog, setShowBankDialog] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchBankAccounts = async () => {
      setBankLoading(true);
      try {
        const tenantId = getCompany()?.id;
        if (!tenantId) return;
        const response = await getBankAccounts(tenantId);
        if (!mounted) return;
        const cuentas = Array.isArray(response?.cuentasBancarias)
          ? response.cuentasBancarias.filter((c) => c.activo)
          : [];
        setBankAccounts(cuentas);
      } catch {
        if (mounted) setBankAccounts([]);
      } finally {
        if (mounted) setBankLoading(false);
      }
    };
    fetchBankAccounts();
    return () => {
      mounted = false;
    };
  }, []);

  const handlePay = () => {
    if (selectedMethod === "credito") {
      onPayWithCredito?.();
      return;
    }
    if (selectedMethod === "transferencia") {
      if (selectedBankAccount) setShowBankDialog(true);
      return;
    }
    onPayWithGetnet?.();
  };

  const isPayDisabled =
    !isPaymentReady ||
    isPaymentBusy ||
    (selectedMethod === "transferencia" && !selectedBankAccount);

  const copyToClipboard = (text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
  };

  const bankDialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Copiar número de cuenta"
        icon="pi pi-copy"
        className="p-button-text"
        onClick={() => copyToClipboard(selectedBankAccount?.numeroCuenta || "")}
      />
      <Button label="Cerrar" onClick={() => setShowBankDialog(false)} />
    </div>
  );

  return (
    <Card className="getnet-card-section getnet-payment-card">
      <h2>Métodos de pago</h2>
      <div>
        <div className="getnet-method-copy">
          <Card className="getnet-method-row">
            <div className="flex align-items-center gap-3">
              <RadioButton
                checked={selectedMethod === "getnet"}
                inputId="getnet-soap"
                readOnly
                onChange={() => setSelectedMethod("getnet")}
              />
              <label htmlFor="getnet-soap" className="getnet-method-label">
                <strong className="getnet-method-title">
                  Tarjeta de crédito, débito o prepago.
                </strong>
                <small className="getnet-map-help">
                  Paga seguro todo lo que necesitas con Getnet utilizando tus
                  tarjetas de crédito, débito y prepago, de todos los emisores
                  nacionales e internacionales.
                </small>
                <img
                  className="getnet-method-logo"
                  src="https://banco.santander.cl/uploads/000/029/870/0620f532-9fc9-4248-b99e-78bae9f13e1d/original/Logo_WebCheckout_Getnet.svg"
                  alt="Getnet"
                />
              </label>
            </div>
          </Card>

          {appCanal === "B2B" ? (
            <Card className="getnet-method-row">
              <div className="flex align-items-center gap-3">
                <RadioButton
                  checked={selectedMethod === "credito"}
                  inputId="pago-credito"
                  readOnly
                  onChange={() => setSelectedMethod("credito")}
                />
                <label htmlFor="pago-credito" className="getnet-method-label">
                  <strong className="getnet-method-title">
                    Pagar a crédito
                  </strong>
                  <small className="getnet-map-help">
                    Usa tu línea de crédito disponible para financiar esta
                    compra. La cotización quedará registrada como pago a
                    crédito.
                  </small>
                  <i
                    className="pi pi-wallet getnet-method-logo"
                    style={{ fontSize: "2rem" }}
                  />
                </label>
              </div>
            </Card>
          ) : null}

          <Card className="getnet-method-row">
            <div className="flex align-items-center gap-3">
              <RadioButton
                checked={selectedMethod === "transferencia"}
                inputId="pago-transferencia"
                readOnly
                onChange={() => setSelectedMethod("transferencia")}
              />
              <label
                htmlFor="pago-transferencia"
                className="getnet-method-label"
              >
                <strong className="getnet-method-title">
                  Transferencia bancaria
                </strong>
                <small className="getnet-map-help">
                  Realiza una transferencia bancaria a una de nuestras cuentas.
                  Verás los datos necesarios para completar el pago.
                </small>
                <i
                  className="pi pi-building getnet-method-logo"
                  style={{ fontSize: "2rem" }}
                />
              </label>
            </div>
          </Card>

          {selectedMethod === "transferencia" ? (
            bankLoading ? (
              <div className="flex align-items-center gap-2 mt-2">
                <i
                  className="pi pi-spin pi-spinner"
                  style={{ fontSize: "1.5rem" }}
                />
                <small className="getnet-map-help">
                  Cargando cuentas bancarias...
                </small>
              </div>
            ) : bankAccounts.length === 0 ? (
              <div className="mt-2">
                <small className="getnet-map-help">
                  No hay cuentas bancarias disponibles en este momento.
                </small>
              </div>
            ) : (
              <div className="flex flex-column gap-2 w-full mt-2">
                {bankAccounts.map((cuenta) => (
                  <Card key={cuenta.id} className="getnet-method-row">
                    <div className="flex align-items-center gap-3">
                      <RadioButton
                        checked={selectedBankAccount?.id === cuenta.id}
                        inputId={`cuenta-${cuenta.id}`}
                        readOnly
                        onChange={() => setSelectedBankAccount(cuenta)}
                      />
                      <label
                        htmlFor={`cuenta-${cuenta.id}`}
                        className="getnet-method-label"
                      >
                        <strong className="getnet-method-title">
                          {cuenta.banco} - {cuenta.tipoCuenta}
                        </strong>
                        <small className="getnet-map-help">
                          {cuenta.titular} · {cuenta.numeroCuenta} ·{" "}
                          {cuenta.moneda}
                        </small>
                      </label>
                    </div>
                  </Card>
                ))}
              </div>
            )
          ) : null}

          <div>

          <Button
            severity="success"
            size="large"
            label="PAGAR"
            rounded
            onClick={handlePay}
            loading={Boolean(paymentLoading)}
            disabled={isPayDisabled}
            icon={"pi pi-wallet"}
            tooltipOptions={{ position: "top" }}
          />
          </div>
        </div>
      </div>

      <Dialog
        header="Datos para transferencia bancaria"
        visible={showBankDialog}
        onHide={() => setShowBankDialog(false)}
        footer={bankDialogFooter}
        style={{ width: "min(92vw, 480px)" }}
      >
        {selectedBankAccount ? (
          <div className="flex flex-column gap-3">
            <p className="m-0 getnet-map-help">
              Realiza la transferencia a la siguiente cuenta bancaria:
            </p>
            <div className="flex flex-column gap-1">
              <span className="getnet-map-help" style={{ fontWeight: 600 }}>
                Banco
              </span>
              <span className="getnet-method-title">
                {selectedBankAccount.banco}
              </span>
            </div>
            <div className="flex flex-column gap-1">
              <span className="getnet-map-help" style={{ fontWeight: 600 }}>
                Tipo de cuenta
              </span>
              <span className="getnet-method-title">
                {selectedBankAccount.tipoCuenta}
              </span>
            </div>
            <div className="flex flex-column gap-1">
              <span className="getnet-map-help" style={{ fontWeight: 600 }}>
                Titular
              </span>
              <span className="getnet-method-title">
                {selectedBankAccount.titular}
              </span>
            </div>
            <div className="flex flex-column gap-1">
              <span className="getnet-map-help" style={{ fontWeight: 600 }}>
                Número de cuenta
              </span>
              <span className="getnet-method-title">
                {selectedBankAccount.numeroCuenta}
              </span>
            </div>
            <div className="flex flex-column gap-1">
              <span className="getnet-map-help" style={{ fontWeight: 600 }}>
                Moneda
              </span>
              <span className="getnet-method-title">
                {selectedBankAccount.moneda}
              </span>
            </div>
            {selectedBankAccount.pais ? (
              <div className="flex flex-column gap-1">
                <span className="getnet-map-help" style={{ fontWeight: 600 }}>
                  País
                </span>
                <span className="getnet-method-title">
                  {selectedBankAccount.pais}
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
      </Dialog>
    </Card>
  );
};

export default CheckoutPaymentSection;
